import { z } from 'zod';

export const SubscriptionPlanEnum = z.enum(['free', 'plus', 'enterprise']);
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanEnum>;

const SubscriptionStatusEnum = z.enum([
  'active',
  'canceled',
  'past_due',
  'trialing',
  'incomplete',
  'incomplete_expired',
  'unpaid',
  'paused',
]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusEnum>;

const INTEGRATION_IDS = [
  'github',
  'linear',
  'supabase',
  'slack',
  'gmail',
  'figma',
  'posthog',
  'render',
  'sentry',
  'karl',
] as const;
export type IntegrationId = (typeof INTEGRATION_IDS)[number];

export type PlanLimitConfig = {
  maxOwnedWorkspaces: number;
  maxTeamsPerWorkspace: number;
  maxProjectsPerTeam: number;
  maxWorkspaceMembers: number;
  maxTeamMembers: number;
  maxProjectMembers: number;
  maxRequirementsPerProject: number;
  maxIntegrations: number;
  allowedIntegrations: readonly IntegrationId[];
};

export const PLAN_LIMITS: Record<SubscriptionPlan, PlanLimitConfig> = {
  free: {
    maxOwnedWorkspaces: 1,
    maxTeamsPerWorkspace: 1,
    maxProjectsPerTeam: 1,
    maxWorkspaceMembers: 1,
    maxTeamMembers: 1,
    maxProjectMembers: 1,
    maxRequirementsPerProject: 10,
    maxIntegrations: 1,
    allowedIntegrations: ['github'],
  },
  plus: {
    maxOwnedWorkspaces: 5,
    maxTeamsPerWorkspace: 5,
    maxProjectsPerTeam: 5,
    maxWorkspaceMembers: Infinity,
    maxTeamMembers: Infinity,
    maxProjectMembers: Infinity,
    maxRequirementsPerProject: Infinity,
    maxIntegrations: 3,
    allowedIntegrations: ['github', 'supabase', 'linear'],
  },
  enterprise: {
    maxOwnedWorkspaces: Infinity,
    maxTeamsPerWorkspace: Infinity,
    maxProjectsPerTeam: Infinity,
    maxWorkspaceMembers: Infinity,
    maxTeamMembers: Infinity,
    maxProjectMembers: Infinity,
    maxRequirementsPerProject: Infinity,
    maxIntegrations: Infinity,
    allowedIntegrations: INTEGRATION_IDS,
  },
};

export const ENTERPRISE_MIN_SEATS = 25;

export const PLAN_DISPLAY = {
  free: {
    name: 'Arvid Free',
    priceLabel: '$0',
    priceSubtext: 'per user / month',
    description: 'For individuals getting started',
  },
  plus: {
    name: 'Arvid Plus',
    priceLabel: '$29',
    priceSubtext: 'per user / month',
    annualPriceLabel: '$19',
    annualPriceSubtext: 'per user / month, billed annually',
    description: 'For growing teams',
  },
  enterprise: {
    name: 'Arvid Power',
    priceLabel: '$49',
    priceSubtext: 'per user / month, billed annually',
    description: 'For organizations at scale',
    minimumSeatsLabel: '25 users minimum',
  },
} as const;

export const PLAN_MARKETING_FEATURES: Record<SubscriptionPlan, readonly string[]> = {
  free: [
    '1 workspace',
    '1 team',
    '1 project',
    '10 requirements',
    'GitHub integration',
  ],
  plus: [
    '5 workspaces',
    '5 teams per workspace',
    '5 projects per team',
    'Unlimited requirements',
    'Unlimited members',
    'Basic integrations',
  ],
  enterprise: [
    'Unlimited workspaces',
    'Unlimited teams',
    'Unlimited projects',
    'Unlimited requirements',
    'Unlimited members',
    'All integrations',
  ],
};

/** Stripe catalog IDs — override via env in server/stripeConfig.ts when set */
export const STRIPE_CONFIG = {
  plusProductId: 'prod_UVGeFoxEjq46QA',
  /** Original Plus monthly ($29/mo) — single canonical monthly price */
  plusPriceId: 'price_1TblvWRCXVHsIms3Qwpv7SOb',
  plusMonthlyPriceId: 'price_1TblvWRCXVHsIms3Qwpv7SOb',
  plusAnnualPriceId: 'price_1TblvWRCXVHsIms3EMRpJuZO',
  enterpriseProductId: 'prod_UYA2sjC3cqBe1s',
  enterpriseAnnualPriceId: 'price_1TblvURCXVHsIms3WHuw5XQ1',
} as const;

const CheckoutPlanEnum = z.enum(['plus', 'enterprise']);
export type CheckoutPlan = z.infer<typeof CheckoutPlanEnum>;

const CheckoutIntervalEnum = z.enum(['month', 'year']);
export type CheckoutInterval = z.infer<typeof CheckoutIntervalEnum>;

export const CreateCheckoutSessionBodySchema = z.object({
  plan: CheckoutPlanEnum.default('plus'),
  interval: CheckoutIntervalEnum.default('month'),
});

const SwitchPlanTargetEnum = z.enum(['plus_annual', 'enterprise']);
export type SwitchPlanTarget = z.infer<typeof SwitchPlanTargetEnum>;

export const SwitchPlanBodySchema = z.object({
  target: SwitchPlanTargetEnum,
});

export const SubscriptionDetailsSchema = z.object({
  plan: SubscriptionPlanEnum,
  status: SubscriptionStatusEnum.nullable().optional(),
  cancelAtPeriodEnd: z.boolean(),
  currentPeriodStart: z.string().nullable().optional(),
  currentPeriodEnd: z.string().nullable().optional(),
  billingInterval: CheckoutIntervalEnum.nullable().optional(),
  stripePriceId: z.string().nullable().optional(),
  quantity: z.number().optional(),
});
export type SubscriptionDetails = z.infer<typeof SubscriptionDetailsSchema>;

function isPaidPlan(plan: SubscriptionPlan): boolean {
  return plan === 'plus' || plan === 'enterprise';
}

export function isActiveSubscriptionPlan(
  plan: SubscriptionPlan,
  status: SubscriptionStatus | null | undefined,
): boolean {
  if (plan === 'free') return true;
  return status === 'active' || status === 'trialing';
}

type PlanSource = {
  plan: SubscriptionPlan;
  status?: SubscriptionStatus | null;
};

/**
 * Merge DB subscription row with Stripe-derived plan details.
 * Stripe wins when the user has an active paid subscription in either source.
 */
export function resolveEffectivePlan(
  subscription: PlanSource | null | undefined,
  stripeDetails?: PlanSource | null,
): SubscriptionPlan {
  const fromDb =
    subscription && isActiveSubscriptionPlan(subscription.plan, subscription.status)
      ? subscription.plan
      : 'free';

  const fromStripe = stripeDetails?.plan;
  const dbPaid =
    subscription != null
    && isActiveSubscriptionPlan(subscription.plan, subscription.status)
    && isPaidPlan(subscription.plan);
  const stripeActive =
    stripeDetails != null
    && isActiveSubscriptionPlan(stripeDetails.plan, stripeDetails.status);

  if (
    fromStripe
    && isPaidPlan(fromStripe)
    && (dbPaid || stripeActive)
  ) {
    return fromStripe;
  }

  return fromDb;
}

export function limitToApiValue(value: number): number | null {
  return value === Infinity ? null : value;
}

export function getPlanLimits(plan: SubscriptionPlan): PlanLimitConfig {
  return PLAN_LIMITS[plan];
}

export function isIntegrationAllowedForPlan(plan: SubscriptionPlan, integrationId: IntegrationId): boolean {
  return PLAN_LIMITS[plan].allowedIntegrations.includes(integrationId);
}

const PlanLimitErrorResponseSchema = z.object({
  error: z.literal('plan_limit_reached'),
  message: z.string(),
  resource: z.string(),
  limit: z.number(),
  current: z.number(),
  upgradePlan: SubscriptionPlanEnum,
});
export type PlanLimitErrorResponse = z.infer<typeof PlanLimitErrorResponseSchema>;

export function parsePlanLimitErrorResponse(body: unknown): PlanLimitErrorResponse | null {
  const parsed = PlanLimitErrorResponseSchema.safeParse(body);
  return parsed.success ? parsed.data : null;
}

export function resolveUpgradePlan(currentPlan: SubscriptionPlan): SubscriptionPlan {
  return currentPlan === 'free' ? 'plus' : 'enterprise';
}

export function formatPlanLimitUpgradeDescription(
  upgradePlan: SubscriptionPlan,
  resourceLabel: string,
): string {
  if (upgradePlan === 'enterprise') {
    return `Upgrade to ${PLAN_DISPLAY.enterprise.name} (${ENTERPRISE_MIN_SEATS}+ seats) for unlimited ${resourceLabel}.`;
  }
  return `Upgrade to ${PLAN_DISPLAY.plus.name} for more ${resourceLabel}.`;
}

export const UserSubscriptionRowSchema = z.object({
  user_id: z.string().uuid(),
  stripe_customer_id: z.string().nullable().optional(),
  stripe_subscription_id: z.string().nullable().optional(),
  plan: SubscriptionPlanEnum,
  status: SubscriptionStatusEnum.nullable().optional(),
  current_period_end: z.string().nullable().optional(),
  cancel_at_period_end: z.boolean().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export const UserSubscriptionSchema = UserSubscriptionRowSchema.transform(row => ({
  userId: row.user_id,
  stripeCustomerId: row.stripe_customer_id ?? undefined,
  stripeSubscriptionId: row.stripe_subscription_id ?? undefined,
  plan: row.plan,
  status: row.status ?? undefined,
  currentPeriodEnd: row.current_period_end ?? undefined,
  cancelAtPeriodEnd: row.cancel_at_period_end ?? false,
  createdAt: row.created_at ?? undefined,
  updatedAt: row.updated_at ?? undefined,
}));

export type UserSubscriptionRow = z.infer<typeof UserSubscriptionRowSchema>;
export type UserSubscription = z.output<typeof UserSubscriptionSchema>;

const PlanLimitsShapeSchema = z.object({
  maxOwnedWorkspaces: z.number().nullable(),
  maxTeamsPerWorkspace: z.number().nullable(),
  maxProjectsPerTeam: z.number().nullable(),
  maxWorkspaceMembers: z.number().nullable(),
  maxTeamMembers: z.number().nullable(),
  maxProjectMembers: z.number().nullable(),
  maxRequirementsPerProject: z.number().nullable(),
  maxIntegrations: z.number().nullable(),
  allowedIntegrations: z.array(z.string()),
});

export const PlanLimitsResponseSchema = z.object({
  plan: SubscriptionPlanEnum,
  limits: PlanLimitsShapeSchema,
  usage: z.object({
    ownedWorkspaces: z.number(),
    integrations: z.number(),
  }),
});
export type PlanLimitsResponse = z.infer<typeof PlanLimitsResponseSchema>;

export const InvoiceSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.string().nullable(),
  amountDue: z.number(),
  amountPaid: z.number(),
  currency: z.string(),
  created: z.number(),
  periodStart: z.number(),
  periodEnd: z.number(),
  hostedInvoiceUrl: z.string().nullable(),
  invoicePdf: z.string().nullable(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export const CheckoutSessionResponseSchema = z.object({
  url: z.string().url(),
});

export const PortalSessionResponseSchema = z.object({
  url: z.string().url(),
});
